import api from '../utils/api';

/**
 * Get all Gantt tasks for a project
 * @param {string} projectId - The ID of the project
 * @returns {Promise<Array>} - Array of Gantt tasks
 */
export const getProjectGanttTasks = async (projectId) => {
  try {
    const response = await api.get(`/gantt-tasks/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching Gantt tasks:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch Gantt tasks');
  }
};

/**
 * Create a new Gantt task
 * @param {string} projectId - The ID of the project
 * @param {Object} taskData - The task data to create
 * @returns {Promise<Object>} - The created task
 */
export const createGanttTask = async (projectId, taskData) => {
  try {
    const response = await api.post(`/gantt-tasks/${projectId}`, taskData);
    return response.data;
  } catch (error) {
    console.error('Error creating Gantt task:', error);
    throw new Error(error.response?.data?.message || 'Failed to create Gantt task');
  }
};

/**
 * Update an existing Gantt task
 * @param {string} projectId - The ID of the project
 * @param {string} taskId - The ID of the task to update
 * @param {Object} taskData - The updated task data
 * @returns {Promise<Object>} - The updated task
 */
export const updateGanttTask = async (projectId, taskId, taskData) => {
  try {
    const response = await api.put(`/gantt-tasks/${projectId}/${taskId}`, taskData);
    return response.data;
  } catch (error) {
    console.error('Error updating Gantt task:', error);
    throw new Error(error.response?.data?.message || 'Failed to update Gantt task');
  }
};

/**
 * Delete a Gantt task
 * @param {string} projectId - The ID of the project
 * @param {string} taskId - The ID of the task to delete
 * @returns {Promise<Object>} - The response data
 */
export const deleteGanttTask = async (projectId, taskId) => {
  try {
    const response = await api.delete(`/gantt-tasks/${projectId}/${taskId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting Gantt task:', error);
    // Check if there are dependent tasks preventing deletion
    if (error.response?.data?.dependentTasks) {
      throw new Error(`Cannot delete task: ${error.response.data.dependentTasks.join(', ')} depend on it`);
    }
    throw new Error(error.response?.data?.message || 'Failed to delete Gantt task');
  }
};

/**
 * Clear all Gantt tasks for a project
 * @param {string} projectId - The ID of the project
 * @returns {Promise<Object>} - The response data
 */
export const clearAllGanttTasks = async (projectId) => {
  try {
    const response = await api.delete(`/gantt-tasks/${projectId}/clear-all`);
    return response.data;
  } catch (error) {
    console.error('Error clearing all Gantt tasks:', error);
    throw new Error(error.response?.data?.message || 'Failed to clear all Gantt tasks');
  }
};
